/**
 * Profile Form Validation Schemas
 */

import { z } from 'zod';

/**
 * Phone number validation for international numbers in E.164 format (+[country code][number])
 */
export const phoneSchema = z
  .string()
  .refine(
    (val) => {
      // Empty or partial prefix treated as no phone provided
      if (!val || val === '' || val === '+') return true;
      return /^\+[1-9]\d{6,14}$/.test(val);
    },
    {
      message: 'Phone must be a valid international number in E.164 format (e.g. +11234567890)',
    }
  )
  .optional();

/**
 * Client Profile validation schema
 */
export const clientProfileSchema = z.object({
  firstName: z
    .string()
    .min(1, 'First name is required')
    .max(50, 'First name must be less than 50 characters'),
  lastName: z
    .string()
    .min(1, 'Last name is required')
    .max(50, 'Last name must be less than 50 characters'),
  email: z.string().email('Invalid email address'),
  phone: phoneSchema,
});

/**
 * Canadian postal code validation (e.g., K1A 0A9 or K1A0A9)
 * Optional - validates format only if provided
 */
const postalCodeSchema = z
  .union([
    z.literal(''),
    z.literal(undefined),
    z.string().regex(/^[A-Z]\d[A-Z]\s?\d[A-Z]\d$/i, {
      message: 'Invalid Canadian postal code format (e.g., K1A 0A9)',
    }),
  ])
  .optional();

/**
 * Contractor Profile validation schema
 * Required: firstName, lastName, email, city, province, coordinates
 * Optional: phone, address, postalCode, serviceArea, profileDescription, hourlyRate
 */
export const contractorProfileSchema = z.object({
  firstName: z
    .string()
    .min(1, 'First name is required')
    .max(50, 'First name must be less than 50 characters'),
  lastName: z
    .string()
    .min(1, 'Last name is required')
    .max(50, 'Last name must be less than 50 characters'),
  email: z.string().email('Invalid email address'),
  phone: phoneSchema,
  // Location fields - address and postal code are optional
  address: z
    .string()
    .max(200, 'Address must be less than 200 characters')
    .optional()
    .or(z.literal('')),
  city: z
    .string()
    .min(1, 'City is required')
    .max(100, 'City must be less than 100 characters'),
  province: z
    .string()
    .length(2, 'Province must be a 2-letter code (e.g., ON, QC)')
    .regex(/^[A-Z]{2}$/, 'Province must be uppercase letters'),
  postalCode: postalCodeSchema,
  // Coordinates are REQUIRED for radius-based contractor search
  latitude: z
    .number()
    .min(-90, 'Latitude must be between -90 and 90')
    .max(90, 'Latitude must be between -90 and 90')
    .nullable()
    .refine((val) => val !== null, {
      message: 'Coordinates are required - use location picker to set them',
    }),
  longitude: z
    .number()
    .min(-180, 'Longitude must be between -180 and 180')
    .max(180, 'Longitude must be between -180 and 180')
    .nullable()
    .refine((val) => val !== null, {
      message: 'Coordinates are required - use location picker to set them',
    }),
  serviceArea: z
    .string()
    .min(1, 'Service area is required')
    .regex(/^\d+(\.\d+)?\s*km$/, {
      message: 'Service area must be in format "XX km"',
    }),
  profileDescription: z
    .string()
    .max(600, 'Description must be less than 600 characters')
    .optional(),
  hourlyRate: z
    .string()
    .regex(/^\d+(\.\d{1,2})?$/, {
      message: 'Hourly rate must be a valid number',
    })
    .optional()
    .or(z.literal('')),
});

/**
 * Service pricing validation
 */
export const servicePriceSchema = z.object({
  price: z
    .string()
    .min(1, 'Price is required')
    .regex(/^\d+(\.\d{1,2})?$/, {
      message: 'Price must be a valid number',
    }),
  unit: z.enum(['per Hour', 'per Service', 'per Project', 'per Day']),
});

/**
 * Category with subcategories validation
 */
export const categoryGroupSchema = z.object({
  categoryId: z.string().min(1, 'Category is required'),
  subcategories: z
    .array(
      z.object({
        subcategoryId: z.string(),
        price: z.string(),
        unit: z.string(),
      })
    )
    .min(1, 'At least one subcategory is required'),
});

export type ClientProfileFormData = z.infer<typeof clientProfileSchema>;
export type ContractorProfileFormData = z.infer<typeof contractorProfileSchema>;
export type ServicePriceFormData = z.infer<typeof servicePriceSchema>;
export type CategoryGroupFormData = z.infer<typeof categoryGroupSchema>;
